import { FieldModel, ProgramBenchmarkName, ProgramTask, TaskDataTraitStatus } from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import Lock from '@mui/icons-material/Lock'
import { Box, Button, Stack } from '@mui/material'
import { MouseEvent, ReactNode, useState } from 'react'
import { InfoNeededCorner } from '../../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../../context'
import { TraitEditorModal } from './TraitEditorModal'

interface TraitCellContainerProps {
  value: any
  children: ReactNode
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  year: number
  selectedField?: FieldModel
  traitId: string
  onAdd?: () => void
  onSaveSuccess?: () => void
}

export const TraitCellContainer = ({
  value,
  children,
  onAdd,
  onSaveSuccess,
  task,
  selectedField,
  year,
  traitId,
}: TraitCellContainerProps) => {
  const { engagement, program } = useProgramEngagementContext()
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const ruleParams = task.rule?.params || {}

  const benchmarkName = ruleParams.benchmarkName
  const benchmarkRequirements = program.data?.benchmarks?.find(
    benchmark => benchmark.benchmark === benchmarkName
  )?.details

  const relevantRequirement =
    benchmarkRequirements &&
    benchmarkRequirements.find((req: any) => req.traitId === traitId && req.year === year)

  const infoNeededV2 =
    selectedField?.id && task && relevantRequirement
      ? engagement.data?.taskIsMissingFieldInfo(
          task.id, // task.id
          selectedField?.resourceId,
          relevantRequirement
        )
      : true

  const handleAdd = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    setIsEditorModalOpen(true)
    onAdd?.()
  }

  const onClose = () => {
    setIsEditorModalOpen(false)
    setAnchorEl(null)
  }

  const handleSuccess = () => {
    onClose()
    onSaveSuccess?.()
  }

  if (!value) {
    return (
      <>
        <Button
          sx={{
            height: 64,
            p: 1,
            m: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
          }}
          onClick={handleAdd}
        >
          {infoNeededV2 && <InfoNeededCorner style={{ marginTop: '-8px', marginLeft: '-18px' }} />}
          <AddIcon color="action" />
        </Button>
        <TraitEditorModal
          isOpen={isEditorModalOpen}
          onClose={onClose}
          onSuccess={handleSuccess}
          anchorEl={anchorEl}
          traitId={traitId}
          year={year}
          selectedField={selectedField}
          task={task}
        />
      </>
    )
  }

  if (value.immutable) {
    return (
      <Box
        sx={{
          height: 64,
          backgroundColor: '#18303814',
          borderRadius: '4px',
          m: 1,
          p: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
        }}
      >
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          {children}

          <Lock fontSize="small" color="disabled" sx={{ opacity: '0.5' }} />
        </Stack>
      </Box>
    )
  }
  return (
    <>
      <Button
        sx={{
          height: 64,
          borderRadius: '4px',
          m: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
          textAlign: 'left',
          color: 'text.primary',
          position: 'relative',
          '&:hover .edit-icon': {
            opacity: 1,
          },
        }}
        onClick={handleAdd}
      >
        {infoNeededV2 && <InfoNeededCorner style={{ marginTop: '-8px', marginLeft: '-18px' }} />}

        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          {children}

          <EditIcon
            className="edit-icon"
            color="action"
            fontSize="small"
            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
          />
        </Stack>
      </Button>
      <TraitEditorModal
        isOpen={isEditorModalOpen}
        onClose={onClose}
        onSuccess={handleSuccess}
        anchorEl={anchorEl}
        traitId={traitId}
        year={year}
        selectedField={selectedField}
        task={task}
      />
    </>
  )
}
